// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Others.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Others component
 */

import React, { Component, PropTypes } from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import EventsUtils from 'EventUtils';
import actions from './Izenda.OthersAction';
import Validator from 'share/components/hoc/Izenda.Validator';
import Input from 'share/components/controls/Izenda.Input';
import Alert from 'share/components/controls/Izenda.Alert';
import IconMenu from 'share/components/controls/Izenda.IconMenu';
import ActionBar from 'share/components/controls/Izenda.ActionBar';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import constants from 'logic/data/Izenda.OtherSettingsConstants';
import classnames from 'classnames';
import { get } from 'lodash/fp';
const ValidatorInput = Validator()(Input);

const OthersAlert = connect()((props: any) => {
  const alertMessage = props.alertMessage;

  return (
    <div className="izenda-Alert-container">
      <Alert type={alertMessage.type}>{alertMessage.message}</Alert>
    </div>
  );
});

const OthersContent = connect()((props: any) => {
  const { store, localization } = props;
  const model = store.otherSettings;
  const constraints = {
    presence: {
      presence: true,
      message: localization.getText('OTHER_SETTINGS_MESSAGE_INTRODUCTION_TEXT_REQUIRED')
    }
  };
  const SAMEFIELD_SAMEDBSCHEMA = 0;
  const SAMEFIELD_SAMEDBSCHEMA_OR_CONNECTIONSTRING = 1;
  const SAMEALIAS = 2;

  const IHS_FIELD_ID = 0;
  const IHS_FIELD_NAME = 1;

  const renderFormgroupItem = (label, title, resetFunction, control, className) => {
    className = classnames('form-group item', className);
    return (
      <div className={className}>
        <span className="col-md-6 col-xs-5 control-label">
          {label} &nbsp; <span className="icon-info-circled" title={title} />
        </span>

        <div className="col-md-4 col-xs-6"> {control} </div>

        <div className="col-md-2 col-xs-1 control-label">
          <span className="icon-ccw resetButton" onClick={resetFunction} title={localization.getText('OTHER_SETTINGS_TOOLTIP_RESET_TO_DEFAULT')} />
        </div>
      </div>
    );
  };

  const resetValueFn = (propKey: string): void => (): void => {
    model.resetValue(propKey);
  };

  const updateValueFn = (propKey: string, paramPath: string): void => (e: any): void => {
    const value = paramPath ? get(paramPath, e) : e;
    model.updateValue(propKey, value);
  };

  const postUpdateValueFn = (propKey: string, paramPath: string): void => (e: any): void => {
    const value = paramPath ? get(paramPath, e) : e;
    model.postUpdateValue(propKey, value);
  };

  const maximumSubreportLevelConstraint = {
    presence: {
      presence: true,
      message: localization.getText('OTHER_SETTINGS_MESSAGE_MAXIMUM_SUBREPORT_LEVEL_REQUIRED')
    },
    numericality: {
      onlyInteger: true,
      get notOnlyInteger() {
        return localization.getText('OTHER_SETTINGS_MESSAGE_MAXIMUM_SUBREPORT_LEVEL_ONLYINTEGER');
      },
      greaterThanOrEqualTo: constants.MAXIMUM_SUBREPORT_LEVEL_MIN_VALUE,
      get notGreaterThanOrEqualTo() {
        return String.format(localization.getText('OTHER_SETTINGS_MESSAGE_MAXIMUM_SUBREPORT_LEVEL_GREATER_THAN'), constants.MAXIMUM_SUBREPORT_LEVEL_MIN_VALUE);
      },
      lessThanOrEqualTo: constants.MAXIMUM_SUBREPORT_LEVEL_MAX_VALUE,
      get notLessThanOrEqualTo() {
        return String.format(localization.getText('OTHER_SETTINGS_MESSAGE_MAXIMUM_SUBREPORT_LEVEL_LESS_THAN'), constants.MAXIMUM_SUBREPORT_LEVEL_MAX_VALUE);
      }
    }
  };

  return (
    <div className="form-horizontal" role="form">
      <div className="form-group">
        <strong className="col-md-6 col-xs-6 control-label">{localization.getText('COMMON_LABEL_NAME')}</strong>
        <strong className="col-md-6 col-xs-6 control-label">{localization.getText('COMMON_LABEL_VALUE')}</strong>
      </div>

      {renderFormgroupItem(
        localization.getText('OTHER_SETTINGS_LABEL_SORT_COLUMN_NAME'),
        localization.getText('OTHER_SETTINGS_TOOLTIP_SORT_COLUMN_NAME'),
        resetValueFn('sortColumnName'),
        <ValidatorInput
          type="checkbox"
          name="trimTimeInJoin"
          onChange={updateValueFn('sortColumnName', EventsUtils.CHECKBOX)}
          checked={store.otherSettings.sortColumnNameValue}
        />
      )}

      {renderFormgroupItem(
        localization.getText('OTHER_SETTINGS_LABEL_TRIM_TIME_IN_JOINS'),
        localization.getText('OTHER_SETTINGS_TOOLTIP_TRIM_TIME_IN_JOINS'),
        resetValueFn('trimTimeInJoins'),
        <ValidatorInput
          type="checkbox"
          name="trimTimeInJoin"
          onChange={updateValueFn('trimTimeInJoins', EventsUtils.CHECKBOX)}
          checked={store.otherSettings.trimTimeInJoinsValue}
        />
      )}

      {renderFormgroupItem(
        localization.getText('OTHER_SETTINGS_LABEL_TIMEZONE_FOR_DATA_OFFSET'),
        localization.getText('OTHER_SETTINGS_TOOLTIP_TIMEZONE_FOR_DATA_OFFSET'),
        resetValueFn('timezoneForDataOffset'),
        <ValidatorInput
          type="text"
          className="form-control"
          name="timezoneForDataOffset"
          onChange={updateValueFn('timezoneForDataOffset', EventsUtils.INPUT)}
          onBlur={postUpdateValueFn('timezoneForDataOffset', EventsUtils.INPUT)}
          value={store.otherSettings.timezoneForDataOffsetValue}
        />
      )}

      {renderFormgroupItem(
        localization.getText('OTHER_SETTINGS_LABEL_TIMEZONE_FOR_TIMESTAMP_OFFSET'),
        localization.getText('OTHER_SETTINGS_TOOLTIP_TIMEZONE_FOR_TIMESTAMP_OFFSET'),
        resetValueFn('timezoneForTimestampOffset'),
        <ValidatorInput
          type="text"
          className="form-control"
          name="timezoneForTimestampOffset"
          onChange={updateValueFn('timezoneForTimestampOffset', EventsUtils.INPUT)}
          onBlur={postUpdateValueFn('timezoneForTimestampOffset', EventsUtils.INPUT)}
          value={store.otherSettings.timezoneForTimestampOffsetValue}
        />
      )}

      {renderFormgroupItem(
        localization.getText('OTHER_SETTINGS_LABEL_CONVERT_NULL_TO_EMPTY_STRING'),
        localization.getText('OTHER_SETTINGS_TOOLTIP_CONVERT_NULL_TO_EMPTY_STRING'),
        resetValueFn('convertNullToEmptyString'),
        <ValidatorInput
          type="checkbox"
          name="convertNullToEmptyString"
          onChange={updateValueFn('convertNullToEmptyString', EventsUtils.CHECKBOX)}
          checked={store.otherSettings.convertNullToEmptyStringValue}
        />
      )}

      {renderFormgroupItem(
        localization.getText('OTHER_SETTINGS_LABEL_ENCODE_VARIABLES_IN_CUSTOM_URL'),
        localization.getText('OTHER_SETTINGS_TOOLTIP_ENCODE_VARIABLES_IN_CUSTOM_URL'),
        resetValueFn('encodeVariablesInCustomUrl'),
        <ValidatorInput
          type="checkbox"
          name="encodeVariablesInCustomUrl"
          onChange={updateValueFn('encodeVariablesInCustomUrl', EventsUtils.CHECKBOX)}
          checked={store.otherSettings.encodeVariablesInCustomUrlValue}
        />
      )}

      {renderFormgroupItem(
        localization.getText('OTHER_SETTINGS_LABEL_SHOW_SCHEMA_NAME'),
        localization.getText('OTHER_SETTINGS_TOOLTIP_SHOW_SCHEMA_NAME'),
        resetValueFn('showSchemaName'),
        <ValidatorInput
          type="checkbox"
          name="showSchemaName"
          onChange={updateValueFn('showSchemaName', EventsUtils.CHECKBOX)}
          checked={store.otherSettings.showSchemaNameValue}
        />
      )}

      {renderFormgroupItem(
        localization.getText('OTHER_SETTINGS_LABEL_SHOW_INTRODUCTION_NAME'),
        localization.getText('OTHER_SETTINGS_TOOLTIP_SHOW_INTRODUCTION_NAME'),
        resetValueFn('showIntroductionText'),
        <ValidatorInput
          type="checkbox"
          name="showIntroductionText"
          onChange={updateValueFn('showIntroductionText', EventsUtils.CHECKBOX)}
          checked={store.otherSettings.showIntroductionTextValue}
        />
      )}

      {store.otherSettings.showIntroductionTextValue &&
        renderFormgroupItem(
          localization.getText('OTHER_SETTINGS_LABEL_INTRODUCTION_TEXT'),
          localization.getText('OTHER_SETTINGS_TOOLTIP_INTRODUCTION_TEXT'),
          resetValueFn('introductionText'),
          <ValidatorInput
            type="text"
            className="form-control"
            name="introductionText"
            validations={constraints}
            field="introductionText"
            onChange={updateValueFn('introductionText', EventsUtils.INPUT)}
            value={store.otherSettings.introductionTextValue}
          />
        )}

      {renderFormgroupItem(
        localization.getText('OTHER_SETTINGS_LABEL_SEND_TO_DISK_PATH'),
        localization.getText('OTHER_SETTINGS_TOOLTIP_SEND_TO_DISK_PATH'),
        resetValueFn('sendToDiskPath'),
        <ValidatorInput
          type="text"
          className="form-control"
          name="sendToDiskPath"
          onChange={updateValueFn('sendToDiskPath', EventsUtils.INPUT)}
          value={store.otherSettings.sendToDiskPathValue}
        />
      )}

      {renderFormgroupItem(
        localization.getText('OTHER_SETTINGS_LABEL_EXCEL_DATA_FILES_PATH'),
        localization.getText('OTHER_SETTINGS_TOOLTIP_EXCEL_DATA_FILES_PATH'),
        resetValueFn('excelDataFilesSystemPath'),
        <ValidatorInput
          type="text"
          className="form-control"
          name="excelDataFilesSystemPath"
          onChange={updateValueFn('excelDataFilesSystemPath', EventsUtils.INPUT)}
          value={store.otherSettings.excelDataFilesSystemPathValue}
        />
      )}

      {renderFormgroupItem(
        localization.getText('OTHER_SETTINGS_LABEL_DETERMINE_COMMON_FILTER'),
        localization.getText('OTHER_SETTINGS_TOOLTIP_DETERMINE_COMMON_FILTER'),
        resetValueFn('commonFilterSetting'),
        <div>
          <label className="izenda-commonFilterItem">
            <input
              type="radio"
              name="commonFilter"
              onChange={updateValueFn('commonFilterSetting', EventsUtils.INPUT)}
              checked={store.otherSettings.commonFilterSettingValue === SAMEFIELD_SAMEDBSCHEMA}
              value="0"
            />
            <span className="control-label">{localization.getText('OTHER_SETTINGS_LABEL_SAMEFIELD_SAMEDBSCHEMA')}</span>
          </label>
          <label className="izenda-commonFilterItem">
            <input
              type="radio"
              name="commonFilter"
              onChange={updateValueFn('commonFilterSetting', EventsUtils.INPUT)}
              checked={store.otherSettings.commonFilterSettingValue === SAMEFIELD_SAMEDBSCHEMA_OR_CONNECTIONSTRING}
              value="1"
            />
            <span className="control-label">{localization.getText('OTHER_SETTINGS_LABEL_SAMEFIELD_SAMEDBSCHEMA_OR_CONNECTIONSTRING')}</span>
          </label>
          <label className="izenda-commonFilterItem">
            <input
              type="radio"
              name="commonFilter"
              onChange={updateValueFn('commonFilterSetting', EventsUtils.INPUT)}
              checked={store.otherSettings.commonFilterSettingValue === SAMEALIAS}
              value="2"
            />
            <span className="control-label">{localization.getText('OTHER_SETTINGS_LABEL_SAMEALIAS')}</span>
          </label>
        </div>,
        'izenda-commonFilter'
      )}

      {renderFormgroupItem(
        localization.getText('OTHER_SETTINGS_LABEL_DETERMINE_INHERIT_FILTER'),
        localization.getText('OTHER_SETTINGS_TOOLTIP_DETERMINE_INHERIT_FILTER'),
        resetValueFn('inheritFilterSetting'),
        <div>
          <label className="izenda-inheritFilterItem">
            <input
              type="radio"
              name="inheritFilter"
              onChange={updateValueFn('inheritFilterSetting', EventsUtils.INPUT)}
              checked={store.otherSettings.inheritFilterSettingValue === IHS_FIELD_ID}
              value="0"
            />
            <span className="control-label">{localization.getText('OTHER_SETTINGS_LABEL_IHS_FIELD_ID')}</span>
          </label>
          <label className="izenda-inheritFilterItem">
            <input
              type="radio"
              name="inheritFilter"
              onChange={updateValueFn('inheritFilterSetting', EventsUtils.INPUT)}
              checked={store.otherSettings.inheritFilterSettingValue === IHS_FIELD_NAME}
              value="1"
            />
            <span className="control-label">{localization.getText('OTHER_SETTINGS_LABEL_IHS_FIELD_NAME')}</span>
          </label>
        </div>,
        'izenda-inheritFilter'
      )}

      {store.otherSettings.isAllowMultiSortDisplayed &&
        renderFormgroupItem(
          localization.getText('OTHER_SETTING_LABEL_MULTIPLE_SORT_ON_GRID_HEADER'),
          localization.getText('OTHER_SETTING_TOOLTIP_MULTIPLE_SORT_ON_GRID_HEADER'),
          resetValueFn('multipleSortOnGridHeader'),
          <ValidatorInput
            type="checkbox"
            name="multipleSortOnGridHeader"
            onChange={updateValueFn('multipleSortOnGridHeader', EventsUtils.CHECKBOX)}
            checked={store.otherSettings.multipleSortOnGridHeaderValue}
          />
        )}

      {model.canShowPreview &&
        renderFormgroupItem(
          localization.getText('OTHER_SETTING_LABEL_SHOW_PREVIEW'),
          localization.getText('OTHER_SETTING_TOOLTIP_SHOW_PREVIEW'),
          resetValueFn('showPreview'),
          <ValidatorInput
            type="checkbox"
            name="showPreview"
            onChange={updateValueFn('showPreview', EventsUtils.CHECKBOX)}
            checked={store.otherSettings.showPreviewValue}
          />
        )}

      {renderFormgroupItem(
        localization.getText('OTHER_SETTING_LABEL_HIDE_HEADER_FOOTER_IN_REPORT'),
        localization.getText('OTHER_SETTING_TOOLTIP_HIDE_HEADER_FOOTER_IN_REPORT'),
        resetValueFn('hideReportHeaderAndFooter'),
        <ValidatorInput
          type="checkbox"
          name="hideReportHeaderAndFooter"
          onChange={updateValueFn('hideReportHeaderAndFooter', EventsUtils.CHECKBOX)}
          checked={store.otherSettings.hideReportHeaderAndFooterValue}
        />
      )}

      {renderFormgroupItem(
          localization.getText('OTHER_SETTING_LABEL_REPEAT_TABLE_HEADER'),
          localization.getText('OTHER_SETTING_TOOLTIP_REPEAT_TABLE_HEADER'),
          resetValueFn('repeatTableHeader'),
          <ValidatorInput
            type="checkbox"
            name="repeatTableHeader"
            onChange={updateValueFn('repeatTableHeader', EventsUtils.CHECKBOX)}
            checked={store.otherSettings.repeatTableHeaderValue}
          />
        )}

      {renderFormgroupItem(
        String.format(localization.getText('OTHER_SETTINGS_LABEL_MAXIMUM_SUBREPORT_LEVEL'),
          constants.MAXIMUM_SUBREPORT_LEVEL_MIN_VALUE, constants.MAXIMUM_SUBREPORT_LEVEL_MAX_VALUE),
        localization.getText('OTHER_SETTINGS_TOOLTIP_MAXIMUM_SUBREPORT_LEVEL'),
        resetValueFn('maximumSubreportLevel'),
        <ValidatorInput
          type="text"
          className="form-control"
          name="maxSubreportLevel"
          value={store.otherSettings.maximumSubreportLevelValue}
          onChange={updateValueFn('maximumSubreportLevel', EventsUtils.INPUT)}
          onBlur={postUpdateValueFn('maximumSubreportLevel', EventsUtils.INPUT)}
          field="maxSubreportLevel"
          validations={maximumSubreportLevelConstraint}
        />
      )}
    </div>
  );
});

@connect(actions)
export default class Others extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired
  };

  componentDidMount() {
    const { loader, store } = this.props;

    loader.startLoading();
    store.init().finally(loader.endLoading.bind(loader));
  }

  componentWillUnmount() {
    this.props.store.destroy();
  }

  render() {
    const props = this.props;
    const model = props.store.otherSettings;

    return (
      <ScrollBox>
        <section className="izenda-Wrapper">
          <div className="izenda-Wrapper-header">
            <IconMenu />
            <OthersAlert alertMessage={model.alertMessage} />
            <ActionBar onSave={props.onSave} onCancel={props.onCancel} />
          </div>

          <div className="izenda-Wrapper-content">
            <div className="u_paddingLeftRight">
              <OthersContent {...props} />
            </div>
          </div>
        </section>
      </ScrollBox>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/AdvancedSetting/Others/Izenda.Others.js